import { Grid, InputAdornment, TextField, MenuItem, FormControl, InputLabel, Select, Button, Typography, Box, CardActions, CardContent, Card, CardHeader, Container } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Loading from "../../../components/loading";
import SuccessMessage from "../../../components/successMessage";
import ErrorMessage from "../../../components/errorMessage";
import api from "../../../api/api";
import IMask from 'imask';
import { useNavigate, useParams } from "react-router-dom";
import dayjs from 'dayjs';

export default function GeneralData({ onSaveSuccess, patient, onPatientLoaded }) {

  const { patientId } = useParams();

  const navigate = useNavigate();

  const cpfMask = useRef(null);
  const responsibleCpfMask = useRef(null);
  const phoneNumberMask = useRef(null);
  const responsiblePhoneNumberMask = useRef(null);

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [gender, setGender] = useState('');
  const [cpf, setCpf] = useState('');
  const [rg, setRg] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [street, setStreet] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [addressComplement, setAddressComplement] = useState('');
  const [indicatedBy, setIndicatedBy] = useState('');
  const [previousDentist, setPreviousDentist] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [firstAppointmentCredit, setFirstAppointmentCredit] = useState('0');
  const [responsibleName, setResponsibleName] = useState('');
  const [responsibleBirthDate, setResponsibleBirthDate] = useState(null);
  const [responsibleCpf, setResponsibleCpf] = useState('');
  const [responsiblePhone, setResponsiblePhone] = useState('');
  const [responsibleKinship, setResponsibleKinship] = useState('');
  const [citiesToComboBox, setCitiesToComboBox] = useState([]);

  const getPatient = useCallback((patientId) => {
    setLoading(true);
    api.get(`/patients/${patientId}`, {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    }).then((returnFromApi) => {
      const patient = returnFromApi.data
      setName(patient.nome);
      setBirthDate(dayjs(patient.dataDeNascimento, "DD/MM/YYYY"));
      setGender(patient.sexo);
      setCpf(patient.cpf);
      setRg(patient.rg);
      setPhoneNumber(patient.telefone);
      setStreet(patient.rua);
      setStreetNumber(patient.numero);
      setNeighborhood(patient.bairro);
      setSelectedCity(patient.cidade);
      setAddressComplement(patient.complementoDeEndereco);
      setIndicatedBy(patient.nomeDeQuemIndicou);
      setPreviousDentist(patient.nomeDoCirurgiaoDentistaAnterior);
      setEmergencyContact(patient.contatoDeEmergencia);
      setFirstAppointmentCredit(patient.creditoDaPrimeiraConsulta);
      setResponsibleName(patient.nomeDoResponsavel);
      setResponsibleBirthDate(dayjs(patient.dataDeNascimentoDoResponsavel, "DD/MM/YYYY"));
      setResponsibleCpf(patient.cpfDoResponsavel);
      setResponsiblePhone(patient.telefoneDoResponsavel);
      setResponsibleKinship(patient.parentescoDoResponsavel);

      if (onPatientLoaded) {
        onPatientLoaded(patient);
      }
      setLoading(false);
    }).catch((returnFromApi) => {
        setLoading(false);
        console.error("ops! ocorreu um erro" + returnFromApi);
        setErrorMessage(returnFromApi.response.data);
        if (returnFromApi.status === 401) {
        navigate("/expired-session");
        }        
    });
  }, [navigate, onPatientLoaded]);


const cleanFields = useCallback(() => {
    setName('');
    setBirthDate(null);
    setGender('');
    setCpf('');
    setRg('');
    setPhoneNumber('');
    setStreet('');
    setStreetNumber('');
    setNeighborhood('');
    setSelectedCity('');
    setAddressComplement('');
    setIndicatedBy('');
    setPreviousDentist('');
    setEmergencyContact('');
    setFirstAppointmentCredit('0');
    setResponsibleName('');
    setResponsibleBirthDate(null);
    setResponsibleCpf('');
    setResponsiblePhone('');
    setResponsibleKinship('');
  }, []);

  const getCities = useCallback(() => {
    api.get("/cities", {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    })
      .then((returnFromApi) => {
        setCitiesToComboBox(returnFromApi.data);
      })
       .catch((returnFromApi) => {
         setLoading(false);
         console.error("ops! ocorreu um erro" + returnFromApi);
         setErrorMessage(returnFromApi.response.data);
         if (returnFromApi.status === 401) {
            navigate("/expired-session");
        }        
      });
  }, [navigate]);

  const applyMaskDefinition = useCallback(() => {
    const masks = [];
    const maskToCPF = '000.000.000-00';
    const maskToPhoneNumber = '(00) 00000-0000';

    if (cpfMask.current) {
      const mask = IMask(cpfMask.current, { mask: maskToCPF });
      mask.on('accept', () => setCpf(mask.value));
      masks.push(mask);
    }

    if (responsibleCpfMask.current) {
      const mask = IMask(responsibleCpfMask.current, { mask: maskToCPF });
      mask.on('accept', () => setResponsibleCpf(mask.value));
      masks.push(mask);
    }

    if (phoneNumberMask.current) {
      const mask = IMask(phoneNumberMask.current, { mask: maskToPhoneNumber });
      mask.on('accept', () => setPhoneNumber(mask.value));
      masks.push(mask);
    }

    if (responsiblePhoneNumberMask.current) {
      const mask = IMask(responsiblePhoneNumberMask.current, { mask: maskToPhoneNumber });
      mask.on('accept', () => setResponsiblePhone(mask.value));
      masks.push(mask);
    }

    return () => masks.forEach((mask) => mask.destroy());
  }, []);

   useEffect(() => {
    if (patient) {
      setName(patient.nome);
    } else {
      cleanFields();
    }
  }, [patient, cleanFields]);

  useEffect(() => {
    applyMaskDefinition();
    getCities();

    if (patientId) {
      getPatient(patientId);
    }
  }, [getPatient, patientId, applyMaskDefinition, getCities]);

   const savePatient = (e) => {
    e.preventDefault();

    if (!name || !name.trim()) {
      setShowErrorMessage(true);
      setErrorMessage('O campo "Nome" é obrigatório.');
      return;
    }

    setLoading(true);

    const patient = {
      "id": patientId,
      "nome": name,
      "cpf": cpf,
      "rg": rg,
      "sexo": gender,
      "dataDeNascimento": birthDate,
      "telefone": phoneNumber,
      "rua": street,
      "numero": streetNumber,
      "bairro": neighborhood,
      "cidade": selectedCity,
      "complementoDeEndereco": addressComplement,
      "nomeDoResponsavel": responsibleName,
      "dataDeNascimentoDoResponsavel": responsibleBirthDate,
      "cpfDoResponsavel": responsibleCpf,
      "telefoneDoResponsavel": responsiblePhone,
      "parentescoDoResponsavel": responsibleKinship,
      "nomeDeQuemIndicou": indicatedBy,
      "contatoDeEmergencia": emergencyContact,
      "nomeDoCirurgiaoDentistaAnterior": previousDentist,
      "creditoDaPrimeiraConsulta": firstAppointmentCredit
    };

    if (patientId) {
      api.put("/patients", patient, {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      }).then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
      }).catch((returnFromApi) => {
          setLoading(false);
          console.error("ops! ocorreu um erro" + returnFromApi);
          setErrorMessage(returnFromApi.response.data);
          if (returnFromApi.status === 401) {
            navigate("/expired-session");
          }        
      });
    } else {
      api.post("/patients", patient, {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      }).then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        cleanFields();
      }).catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response.data);
        setLoading(false);
        console.error(returnFromApi);
      });
    }
  };

  return (
    <Container maxWidth="lg" >
      <Card component="form" onSubmit={savePatient} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title={<Typography variant="h5" sx={{ fontWeight: 'bold' }}>Dados do Paciente</Typography>} Dados do Paciente
        />
        <CardContent>
          <Loading isToHidden={isLoading === false} />
          {showSuccessMessage && <SuccessMessage message={successMessage} show={showSuccessMessage} setShow={setShowSuccessMessage} />}
          {showErrorMessage && <ErrorMessage message={errorMessage} show={showErrorMessage} setShow={setShowErrorMessage} />}

          <Grid container spacing={4}>
            <Grid size={3}>
              <TextField fullWidth label="Nome" name="name" value={name} onChange={(e) => setName(e.target.value)} />
            </Grid>

            <Grid size={2}>
              <TextField
                label="CPF"
                value={cpf}
                inputRef={cpfMask}
                onChange={(e) => setCpf(e.target.value)} />
            </Grid>

            <Grid size={2.7}>
              <DatePicker label="Data de Nascimento" value={birthDate} onChange={(newValue) => setBirthDate(newValue)} slotProps={{ textField: { fullWidth: true } }} />
            </Grid>

            <Grid size={2.2}>
              <TextField fullWidth label="RG" name="rg" value={rg} onChange={(e) => setRg(e.target.value)} />
            </Grid>

            <Grid size={2}>
              <FormControl fullWidth>
                <InputLabel>Sexo</InputLabel>
                <Select label="Sexo"
                  name="gender"
                  value={gender ?? ''}
                  onChange={(e) => setGender(e.target.value)}>
                  <MenuItem value="MASCULINO">Masculino</MenuItem>
                  <MenuItem value="FEMININO">Feminino</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={3}>
              <TextField fullWidth label="Rua" name="street" value={street} onChange={(e) => setStreet(e.target.value)} />
            </Grid>

            <Grid size={1.7}>
              <TextField fullWidth label="Número" name="number" value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} />
            </Grid>

            <Grid size={2.3}>
              <TextField fullWidth label="Bairro" name="neighborhood" value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} />
            </Grid>

            <Grid size={2.4}>
              <FormControl fullWidth>
                <InputLabel>Cidade</InputLabel>
                <Select label="Cidade" name="city"
                  value={selectedCity ?? ''}
                  onChange={(e) => setSelectedCity(e.target.value)}>
                  {citiesToComboBox.map((city) => (
                    <MenuItem value={city.value}>{city.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={2.5}>
              <TextField fullWidth label="Complemento" name="complement" value={addressComplement} onChange={(e) => setAddressComplement(e.target.value)} />
            </Grid>

            <Grid size={2.7}>
              <TextField fullWidth
                label="Telefone"
                name="phone"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                inputRef={phoneNumberMask} />
            </Grid>

            <Grid size={3.1}>
              <TextField fullWidth label="Indicado por" name="indicatedBy" value={indicatedBy} onChange={(e) => setIndicatedBy(e.target.value)} />
            </Grid>

            <Grid size={3.7}>
              <TextField fullWidth label="Cirurgião Dentista Anterior" name="previousDentist" value={previousDentist} onChange={(e) => setPreviousDentist(e.target.value)} />
            </Grid>

            <Grid size={2.4}>
              <TextField fullWidth label="Crédito da primeira consulta" type="number" name="firstConsultationCredit" value={firstAppointmentCredit} onChange={(e) => setFirstAppointmentCredit(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>

            <Grid size={11.9}>
              <TextField fullWidth label="Em caso de emergência, contatar" name="emergencyContact" value={emergencyContact} onChange={(e) => setEmergencyContact(e.target.value)} multiline rows={2} />
            </Grid>

          </Grid>

          <Box sx={{ mt: 5 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 5, fontWeight: 'bold' }}>
              Dados do Responsável
            </Typography>

            <Grid container spacing={3}>

              <Grid size={3.2}>
                <TextField fullWidth label="Nome do Responsável" name="responsibleName" value={responsibleName} onChange={(e) => setResponsibleName(e.target.value)} />
              </Grid>

              <Grid size={3.85}>
                <DatePicker label="Data de Nascimento do Responsável" value={responsibleBirthDate} onChange={(newValue) => setResponsibleBirthDate(newValue)} slotProps={{ textField: { fullWidth: true } }} />
              </Grid>

              <Grid size={2.5}>
                <TextField fullWidth
                  label="CPF do Responsável"
                  name="responsibleCpf"
                  value={responsibleCpf}
                  onChange={(e) => setResponsibleCpf(e.target.value)}
                  inputRef={responsibleCpfMask} />
              </Grid>

              <Grid size={2.35}>
                <TextField fullWidth
                  label="Telefone"
                  name="responsiblePhone"
                  value={responsiblePhone}
                  onChange={(e) => setResponsiblePhone(e.target.value)}
                  inputRef={responsiblePhoneNumberMask} />
              </Grid>

              <Grid size={11.9}>
                <TextField fullWidth label="Parentesco" name="responsibleKinship" value={responsibleKinship} onChange={(e) => setResponsibleKinship(e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        </CardContent>
        <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
          <Button variant="contained" type="submit">Salvar</Button>
        </CardActions>
      </Card>
    </Container>
  );
}